import React, { useEffect, useState } from "react";
import { api, unwrapObject, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  Save,
  Globe2,
  Coins,
  CreditCard,
  Mail,
  Phone,
  FileText,
  Loader2,
} from "lucide-react";
import { INDIAN_STATES, stateFromGstin } from "@/lib/indianStates";
import type { ApiResponse, Country, Currency } from "@/types";

export function CompanyPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);

  const [form, setForm] = useState({
    id: "",
    name: "",
    address: "",
    country_id: "",
    currency_id: "",
    postal_code: "",
    email: "",
    website: "",
    uen_no: "",
    gst_no: "",
    state: "",
    bank_name: "",
    bank_acct_no: "",
    telephone_no: "",
    fax_no: "",
    invoice_footer: "",
  });

  const { success, error } = useToast();

  const loadData = async () => {
    setLoading(true);
    try {
      const [compRes, countryRes, currRes] = await Promise.all([
        api.get("/api/Company/Get"),
        api.get("/api/Country/List"),
        api.get("/api/Currency/List"),
      ]);

      const comp = unwrapObject<any>(compRes);
      setForm({
        id: comp.id || "",
        name: comp.name || comp.CompanyName || "",
        address: comp.address || comp.AddressLine1 || "",
        country_id: String(comp.country_id || comp.CountryId || ""),
        currency_id: String(comp.currency_id || comp.CurrencyId || ""),
        postal_code: comp.postal_code || comp.PostalCode || "",
        email: comp.email || comp.Email || "",
        website: comp.website || comp.Website || "",
        uen_no: comp.uen_no || comp.RegistrationNumber || comp.GstNumber || "",
        gst_no: comp.gst_no || "",
        state: comp.state || "",
        bank_name: comp.bank_name || comp.BankName || "",
        bank_acct_no: comp.bank_acct_no || comp.BankAccountNumber || "",
        telephone_no: comp.telephone_no || comp.Phone || "",
        fax_no: comp.fax_no || "",
        invoice_footer: comp.invoice_footer || "",
      });

      setCountries(unwrapArray<Country>(countryRes));
      setCurrencies(unwrapArray<Currency>(currRes));
    } catch (err: any) {
      error("Fetch Error", err.message || "Failed to load company details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.country_id || !form.currency_id) {
      error("Validation Error", "Please fill required company fields (Name, Email, Country, Currency)");
      return;
    }

    setSaving(true);
    try {
      await api.patch("/api/Company/Update", form);
      success("Profile Updated", "Company profile and legal details saved successfully");
      await loadData();
    } catch (err: any) {
      error("Save Failed", err.message || "Failed to update company settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <FormSkeleton />;
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-foreground">
            Company Configuration
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Maintain your legal entity profile, tax identifiers, banking coordinates, and billing footer
          </p>
        </div>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          className="shadow-sm gap-2 h-9"
        >
          {saving ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Save className="size-4" />
          )}
          Save Changes
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section 1: Basic & Registration Info */}
        <Card className="shadow-xs">
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className="size-8 rounded-lg bg-blue-500/10 text-primary flex items-center justify-center">
                <Building2 className="size-4" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">General & Legal Profile</CardTitle>
                <CardDescription>Primary operating business information</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Company Legal Name *</Label>
                <Input
                  required
                  placeholder="Nelgates Enterprise"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Registration / UEN No.</Label>
                <Input
                  placeholder="e.g. 201829384G"
                  value={form.uen_no}
                  onChange={(e) => setForm({ ...form, uen_no: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">GSTIN</Label>
                <Input
                  placeholder="27AAACN1234F1Z5"
                  maxLength={15}
                  value={form.gst_no}
                  onChange={(e) => {
                    const gst_no = e.target.value.toUpperCase();
                    setForm({ ...form, gst_no, state: form.state || stateFromGstin(gst_no) });
                  }}
                  className="font-mono uppercase"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">GST Registration State</Label>
                <select
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">-- Select state --</option>
                  {INDIAN_STATES.map((st) => (
                    <option key={st.code} value={st.name}>
                      {st.name}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-muted-foreground">
                  Quotes to customers in this state use CGST + SGST; other states use IGST
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Default Country *</Label>
                <Select
                  value={form.country_id}
                  onValueChange={(val) => setForm({ ...form, country_id: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((c: any) => (
                      <SelectItem key={c.id || c.Id} value={String(c.id || c.Id)}>
                        {c.name || c.CountryName} ({c.code || c.CountryCode})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Base Currency *</Label>
                <Select
                  value={form.currency_id}
                  onValueChange={(val) => setForm({ ...form, currency_id: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c: any) => (
                      <SelectItem key={c.id || c.Id} value={String(c.id || c.Id)}>
                        {c.code || c.CurrencyCode} - {c.name || c.CurrencyName} ({c.symbol || c.CurrencySymbol})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Contact & Address Details */}
        <Card className="shadow-xs">
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className="size-8 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                <Mail className="size-4" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">Location & Communication</CardTitle>
                <CardDescription>Official mailing and contact endpoints</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Corporate Email *</Label>
                <Input
                  required
                  type="email"
                  placeholder="contact@nelgates.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Telephone Number</Label>
                <Input
                  placeholder="+123456789"
                  value={form.telephone_no}
                  onChange={(e) => setForm({ ...form, telephone_no: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Website</Label>
                <Input
                  placeholder="https://nelgates.com"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-xs font-semibold">Registered Address *</Label>
                <Input
                  required
                  placeholder="Suite 500, Industrial Textile Park"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Postal / ZIP Code *</Label>
                <Input
                  required
                  placeholder="600001"
                  value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Banking & Billing */}
        <Card className="shadow-xs">
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className="size-8 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                <CreditCard className="size-4" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">Banking Coordinates & Invoice Footer</CardTitle>
                <CardDescription>Wire details displayed on quotations and invoice printouts</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Bank Name</Label>
                <Input
                  placeholder="e.g. Standard Chartered"
                  value={form.bank_name}
                  onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Bank Account Number</Label>
                <Input
                  placeholder="e.g. 091-8829-1002"
                  value={form.bank_acct_no}
                  onChange={(e) => setForm({ ...form, bank_acct_no: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Invoice Footer / Remittance Instructions</Label>
              <Textarea
                placeholder="Thank you for your business. Terms: Net 30 days."
                rows={3}
                value={form.invoice_footer}
                onChange={(e) => setForm({ ...form, invoice_footer: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
